import React, {useMemo, useState} from "react";
import {useAuth} from "./AuthProvider";
import {useProject} from "./ClientProjectProvider";
import TeamProvider from "./TeamProvider";
import {useMutation, useQueryClient} from "@tanstack/react-query";
import {useLocation, useNavigate, useParams, useSearchParams} from "react-router-dom";


let TeamCandidateContext = React.createContext(null);

const TeamCandidateProvider = ({children, criteria, team, onUpdated, ...props}) => {

    const {auth} = useAuth();

    const queryClient = useQueryClient();
    const navigate = useNavigate();
    const location = useLocation();

    const params = useParams();

    const [searchParams, setSearchParams] = useSearchParams();
    const {project} = useProject();
    const [isUpdating, setIsUpdating] = useState(false);


    /*
    |--------------------------------------------------------------------------
    | Handling Requests - Team
    |--------------------------------------------------------------------------
    */
    const teamCandidateMutation = useMutation(['teamCandidateRequest', team?.id],
        async({method, successKey}) => {

            let updatedCandidate = await team[method](
                project
            )

            if (updatedCandidate) {
                if (successKey) TB.sysAlert(successKey);
                return updatedCandidate
            }

        }, {
            onError: async (e) => {
                await setIsUpdating(false)
                return TB.showServerErrorToast()
            },
            onSuccess: async (updatedCandidate) => {
                await setIsUpdating(false)
                // if (!updatedCandidate) {
                //     TB.showServerErrorToast()
                //     return false;
                // }
            }
        }
    )

    const runProjectTeamRequest = async(method, successKey) => {
        await setIsUpdating(true)

        let updatedCandidate = await teamCandidateMutation.mutateAsync({method, successKey}, {
            onError: async (e) => {
                await setIsUpdating(false)
                return TB.showServerErrorToast()
            },
            onSuccess: async (updatedCandidate) => {
                await setIsUpdating(false)
                // if (!updatedCandidate) {
                //     TB.showServerErrorToast()
                //     return false;
                // }
            }
        });

        console.log('runProjectTeamRequest', updatedCandidate);

        await setIsUpdating(false)

        return updatedCandidate
    }

    const addToFavorites = async () => {
        return runProjectTeamRequest(
            'addToFavorites',  'byo.favorites_added'
        );
    }

    const removeFromFavorites = async () => {
        return runProjectTeamRequest(
            'removeFromFavorites',  'byo.favorites_removed'
        );
    }

    const addToLiked = async () => {
        return runProjectTeamRequest(
            'addToLiked',  'team_shopping.shortlist_added'
        );
    }

    const removeFromLiked = async () => {
        return runProjectTeamRequest(
            'removeFromLiked',  'team_shopping.shortlist_removed'
        );
    }

    const addToArchive = async () => {
        return runProjectTeamRequest(
            'addToArchive',  'team_shopping.archive_added'
        );
    }

    const removeFromArchive = async () => {
        return runProjectTeamRequest(
            'removeFromArchive',  'team_shopping.archive_removed'
        );
    }

    const addToCandidates = async() => {
        let canAddToCandidates = await project.canShortlistCandidates();
        if (!canAddToCandidates) {
            return;
        }
        let hasSeenTip = await TB.sysAlert('team_shopping.add_to_proposal_button_clicked_tip')

        if (hasSeenTip) {
            return runProjectTeamRequest(
                'addToCandidates', 'team_shopping.add_to_proposal_button_clicked'
            );
        }
    }

    const removeFromCandidates = async() => {
        let candidate = await runProjectTeamRequest(
            'removeFromCandidates', 'team_shopping.cancel_proposal_button_clicked'
        );

        if (candidate) {
            await TB.queryClient.invalidateQueries({
                predicate: ({queryKey}) => {
                    let [key, params] = queryKey;

                    if(key === 'teamMatches' && params.project === project.id) {
                        console.log('invalidatingQuery')
                    }
                    return key === 'teamMatches' && params.project === project.id;
                }
            })

        }
        return candidate;
    }
    const assignCandidate = async() => {

        let updated = await runProjectTeamRequest('assignTo')

        if (updated) {
            await TB.alert('success','Team Proposal Accepted', 'You have selected a team for your project. You can now request an SOW for your project.', [
                {title: 'Ok'}
            ])
            await queryClient.invalidateQueries(['getConversation'])
        }
    }

    const unassignCandidate = async() => {
        if (project?.hasRequestedSOW()) {
            return TB.toast("Can't reopen after SOW has already been requested!", {theme: 'danger'})
        }
        if (project.assigned_team_candidate?.job_candidate?.id !== team?.job_candidate?.id) {
            return;
        }

        let updated = runProjectTeamRequest('unassignFrom');

        if (updated) {
            TB.toast("Project Reopened")
            return updated
        }
    }
    const canFavorite = auth?.isClient();
    const permissionToInvite = project && auth?.canInviteTalentForProject(project);

    const canArchive = useMemo(() => {
        return permissionToInvite && (team?.job_candidate?.was_disliked || !team.disabledFromInvite());
    }, [
        auth,
        team?.job_candidate?.was_liked,
        team?.job_candidate?.was_disliked,
        team?.job_candidate?.is_candidate,
        team?.job_candidate?.status,
    ]);


    let value = {
        team,
        criteria,
        isDisabled: isUpdating || teamCandidateMutation.isLoading,
        addToFavorites,
        removeFromFavorites,
        addToLiked,
        removeFromLiked,
        assignCandidate,
        unassignCandidate,
        addToArchive,
        removeFromArchive,
        addToCandidates,
        removeFromCandidates,
        canFavorite,
        canArchive
    }

    return (
        <TeamCandidateContext.Provider value={value}>
            <TeamProvider team={team} onUpdated={onUpdated} criteria={criteria}>
                {children}
            </TeamProvider>
        </TeamCandidateContext.Provider>
    );
}

const useTeamCandidate = () => {
    return React.useContext(TeamCandidateContext);
}
export default TeamCandidateProvider;
export {
    useTeamCandidate,
    TeamCandidateContext
}